import { KeyboardEvent, useContext, useMemo, useState } from 'react';

import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import * as Yup from 'yup';

import { Autocomplete, Box, Button, Chip, Divider, List, ListItemButton, Stack, TextField, Typography, styled } from '@mui/material';

import { ConfirmDialog } from '../../../components/dialogs';
import { AlertSnackbarContext } from '../../../components/snackbars';
import { Defaults } from '../../../constants';
import { useGetCategoriesByPage } from '../../../lib/category/hooks';
import { useGetSubCategoriesByCategory } from '../../../lib/category/hooks/useGetSubCategoriesByCategory';
import { useUpdatePost } from '../../../lib/post/hooks';
import useGetAllTopics from '../../../lib/topic/hooks/useGetAllTopics';
import { Category } from '../../../types/Category';
import { Hashtag } from '../../../types/Hashtag';
import { Post } from '../../../types/Post';
import { Topic } from '../../../types/Topic';

import AddParentCategoryButton from './AddParentCategoryButton';
import RemoveParentCategoryButton from './RemoveParentCategoryButton';

type Props = {
  post?: Post;
};

export default function PostSidebar({ post }: Props) {
  const navigate = useNavigate();
  const [, setAlertSnackbar] = useContext(AlertSnackbarContext);
  const [selectedCategoryId, setSelectedCategoryId] = useState(post?.category?.id || '');
  const { mutate: updatePost } = useUpdatePost({
    onSuccess: () => {
      setAlertSnackbar({
        message: 'Article updated',
        severity: 'success',
        vertical: 'top',
      });
      navigate(-1);
    },
  });

  // Category
  const [searchCategory, setSearchCategory] = useState('');
  const debouncedSearchCategoryFn = useDebouncedCallback((value: string) => {
    setSearchCategory(value);
  }, Defaults.SEARCH_DEBOUNCE_MS);
  const { data: categories } = useGetCategoriesByPage({
    page: 0,
    sort: '',
    search: searchCategory,
  });

  // SubCategory
  const [searchSubCategory, setSearchSubCategory] = useState('');
  const debouncedSearchSubCategoryFn = useDebouncedCallback((value: string) => {
    setSearchSubCategory(value);
  }, Defaults.SEARCH_DEBOUNCE_MS);
  const { data: subCategories } = useGetSubCategoriesByCategory(selectedCategoryId, {
    page: 0,
    search: searchSubCategory,
  });
  const { data: allSubCategories } = useGetSubCategoriesByCategory(selectedCategoryId, {
    page: 0,
    search: '',
  });
  const requiresSubCategory = (allSubCategories?.total ?? 0) > 0;
  const validationSchema = useMemo(
    () =>
      Yup.object({
        category: Yup.string().required('Category is required'),
        subCategory: requiresSubCategory ? Yup.string().required('Sub category is required') : Yup.string().optional(),
        topics: Yup.array().of(Yup.string()).min(1, 'Select at least one topic'),
        description: Yup.string().max(500, 'Maximum 500 characters').optional(),
        hashtags: Yup.array().of(Yup.string()).optional(),
      }),
    [requiresSubCategory],
  );

  // initial values use post if provided
  const formik = useFormik({
    initialValues: {
      category: post?.category?.id || '',
      subCategory: post?.subCategory?.id || '',
      topics: post?.topics?.map((t) => t.id) || [],
      description: post?.description || '',
      hashtags: post?.hashtags?.map((h) => h.name) || [],
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: (values: Yup.InferType<typeof validationSchema>) => {
      updatePost({
        postId: post?.id || '',
        params: {
          categoryId: values.category,
          subCategoryId: values.subCategory || undefined,
          topic: values.topics?.map((t) => ({ id: t }) as Topic),
          description: values.description,
          hashtag: values.hashtags?.map((h) => ({ name: h }) as Hashtag),
        },
      });
    },
  });

  // Topic
  const [searchTopic, setSearchTopic] = useState('');
  const debouncedSearchTopicFn = useDebouncedCallback((value: string) => {
    setSearchTopic(value);
  }, Defaults.SEARCH_DEBOUNCE_MS);
  const { data: topics } = useGetAllTopics({
    page: 0,
    sort: '',
    search: searchTopic,
  });
  const topicsOptions: Topic[] = topics?.data ?? [];

  const selectedTopics = topicsOptions.filter((c) => (formik.values.topics as string[]).includes(c.id));

  const [hashtagInput, setHashtagInput] = useState<string>('');

  const addHashtagFromInput = () => {
    let tag = hashtagInput.trim();
    if (!tag) return;

    // validate format
    const valid = /^[A-Za-z0-9_]+$/.test(tag);
    if (!valid) {
      formik.setFieldError('hashtags', 'Only letters, numbers and underscores allowed');
      setAlertSnackbar({
        message: 'Hashtag can only contain letters, numbers and underscores',
        severity: 'error',
        vertical: 'bottom',
      });
      return;
    }

    if (formik.values.hashtags?.includes(`#${tag}`)) {
      formik.setFieldError('hashtags', 'Tag already added');
      setAlertSnackbar({
        message: 'Tag already added',
        severity: 'error',
        vertical: 'bottom',
      });
      return;
    }

    formik.setFieldValue('hashtags', [...(formik.values.hashtags || []), `#${tag}`]);
    // clear possible hashtag errors if any
    formik.setFieldError('hashtags', undefined as any);
    setHashtagInput('');
  };

  const handleHashtagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addHashtagFromInput();
    }
    if (e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addHashtagFromInput();
    }
  };

  const removeHashtag = (tag: string) => {
    formik.setFieldValue('hashtags', formik.values.hashtags?.filter((t?: string) => t !== tag));
    // clear error if removal fixes it
    if ((formik.values.hashtags as string[]).length - 1 < 5) {
      formik.setFieldError('hashtags', undefined as any);
    }
  };

  const additionalParentCategories =
    post?.parentCategories?.filter((parentCategory) => parentCategory.id !== post?.category?.id) ??
    post?.additionalParents ??
    post?.additionalParentCategories ??
    [];

  return (
    <SidebarContainer>
      <form>
        <Box sx={{ marginBottom: 2 }}>
          <Autocomplete<Category>
            disablePortal
            options={categories ?? []}
            getOptionLabel={(option) => option.title}
            value={categories?.find((c) => c.id === formik.values.category) || null}
            onChange={(_, value) => {
              const categoryId = value ? value.id : '';
              setSelectedCategoryId(categoryId);
              formik.setFieldValue('category', categoryId);
              formik.setFieldValue('subCategory', ''); // reset subcategory
              setSearchSubCategory('');
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                error={formik.touched.category && !!formik.errors.category}
                type="text"
                name="category"
                label="Category"
                onChange={(e) => {
                  formik.handleChange(e);
                  debouncedSearchCategoryFn(e.target.value);
                }}
                onBlur={formik.handleBlur}
                value={formik.values.category}
                helperText={formik.touched.category ? (formik.errors.category as string | undefined) : undefined}
              />
            )}
          />
        </Box>

        <Box sx={{ marginBottom: 2 }}>
          <Autocomplete<Category>
            disablePortal
            options={subCategories?.data ?? []}
            getOptionLabel={(option) => option.title}
            value={subCategories?.data.find((c) => c.id === formik.values.subCategory) || null}
            onChange={(_, value) => {
              formik.setFieldValue('subCategory', value ? value.id : '');
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                error={formik.touched.subCategory && !!formik.errors.subCategory}
                type="text"
                name="subCategory"
                label={requiresSubCategory ? 'Subcategory' : 'Subcategory (optional)'}
                onChange={(e) => {
                  formik.handleChange(e);
                  debouncedSearchSubCategoryFn(e.target.value);
                }}
                onBlur={formik.handleBlur}
                value={formik.values.subCategory}
                helperText={formik.touched.subCategory ? (formik.errors.subCategory as string | undefined) : undefined}
              />
            )}
          />
        </Box>

        <Box sx={{ marginBottom: 2 }}>
          <Stack flexDirection="row" alignItems="center" justifyContent="space-between" spacing={1} marginBottom={1}>
            <Typography variant="subtitle1">Additional Parent Categories</Typography>
            <AddParentCategoryButton
              postId={post?.id || ''}
              primaryCategoryId={formik.values.category}
              existingParentCategoryIds={additionalParentCategories.map((parentCategory) => parentCategory.id)}
            />
          </Stack>
          {additionalParentCategories.length ? (
            <List sx={{ marginTop: 0 }}>
              {additionalParentCategories.map((parentCategory) => (
                <Box key={parentCategory.id}>
                  <ListItemButton>
                    <Stack flexDirection="row" justifyContent="space-between" alignItems="center" width="100%">
                      <Typography>{parentCategory.title}</Typography>
                      <RemoveParentCategoryButton parentCategoryId={parentCategory.id} postId={post?.id || ''} />
                    </Stack>
                  </ListItemButton>
                  <Divider />
                </Box>
              ))}
            </List>
          ) : (
            <Typography variant="body2">No additional parent categories</Typography>
          )}
        </Box>

        <Box sx={{ marginBottom: 2 }}>
          <Autocomplete
            multiple
            options={topicsOptions ?? []}
            getOptionLabel={(option) => option.title}
            value={topicsOptions.filter((c) => (formik.values.topics as string[]).includes(c.id)) ?? []}
            onChange={(_, value) => {
              formik.setFieldValue('topics', value ? value.map((v) => v.id) : []);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderTags={() => null}
            renderInput={(params) => (
              <TextField
                {...params}
                error={formik.touched.topics && !!formik.errors.topics}
                type="text"
                name="topics"
                label="Topics"
                placeholder="Select topics"
                onChange={(e) => {
                  formik.handleChange(e);
                  debouncedSearchTopicFn(e.target.value);
                }}
                onBlur={formik.handleBlur}
                value={formik.values.topics}
                helperText={formik.touched.topics ? (formik.errors.topics as string | undefined) : undefined}
              />
            )}
          />
          <Stack direction="row" rowGap={1} spacing={0.5} sx={{ marginTop: '8px', flexWrap: 'wrap' }}>
            {selectedTopics.map((t: Topic) => (
              <Chip
                key={t.id}
                label={t.title}
                onDelete={() => formik.setFieldValue('topics', formik.values.topics?.filter((x?: string) => x !== t.id))}
              />
            ))}
          </Stack>
        </Box>

        <Box sx={{ marginBottom: 2 }}>
          <Typography variant="subtitle1">Description</Typography>
          <TextField
            multiline
            minRows={3}
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Short description"
            fullWidth
            error={Boolean(formik.touched.description && formik.errors.description)}
            helperText={formik.touched.description && (formik.errors.description as string)}
          />
        </Box>

        <Box sx={{ marginBottom: 2 }}>
          <Typography variant="subtitle1">Hashtags</Typography>
          <TextField
            placeholder="Type a tag and press Enter"
            value={hashtagInput}
            onChange={(e) => {
              setHashtagInput(e.target.value);
              // clear tag-specific error while typing
              if (formik.errors.hashtags) formik.setFieldError('hashtags', undefined as any);
            }}
            onKeyDown={handleHashtagKeyDown}
            fullWidth
            type="text"
          />
          {formik.touched.hashtags && formik.errors.hashtags ? (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
              {typeof formik.errors.hashtags === 'string' ? formik.errors.hashtags : ''}
            </Typography>
          ) : null}
          <Stack direction="row" rowGap={1} spacing={0.5} sx={{ marginTop: '8px', flexWrap: 'wrap' }}>
            {formik.values.hashtags?.map((tag?: string) => <Chip key={tag} label={`${tag}`} onDelete={() => tag && removeHashtag(tag)} />)}
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', marginTop: 1 }}>
          <ConfirmDialog
            title="Edit Article"
            description={`Are you sure you want to save the changes to this article?`}
            trigger={(handleOpen) => (
              <Button
                onClick={() => {
                  // check for validation errors before opening dialog
                  formik.validateForm().then((errors) => {
                    if (Object.keys(errors).length === 0) {
                      handleOpen();
                    } else {
                      // mark all fields as touched to show errors
                      formik.setTouched({
                        category: true,
                        subCategory: true,
                        topics: true,
                        description: true,
                        hashtags: true,
                      });
                    }
                  });
                }}
                type="button"
                variant="contained"
                disabled={false}
              >
                Save
              </Button>
            )}
            onConfirm={formik.submitForm}
            cancelBtnText="Cancel"
            submitBtnText="Save"
          />
        </Stack>
      </form>
    </SidebarContainer>
  );
}

const SidebarContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});
